import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

const RegistrationConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resendVerificationEmail } = useAuth();
  
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [canResend, setCanResend] = useState(true);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    // Get email from navigation state or redirect to register
    const emailFromState = location?.state?.email;
    if (!emailFromState) {
      navigate('/register');
      return;
    }
    setEmail(emailFromState);
  }, [location, navigate]);

  useEffect(() => {
    // Countdown timer for resend cooldown
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !canResend) {
      setCanResend(true);
    }
  }, [countdown, canResend]);

  const handleResendVerification = async () => {
    if (!canResend || isResending) return;

    setIsResending(true);
    setResendError('');
    setResendSuccess(false);

    try {
      const { error } = await resendVerificationEmail(email);
      
      if (error) {
        setResendError(error?.message || 'Failed to resend verification email. Please try again.');
      } else {
        setResendSuccess(true);
        setCanResend(false);
        setCountdown(60); // 60 second cooldown
        
        // Clear success message after 5 seconds
        setTimeout(() => setResendSuccess(false), 5000);
      }
    } catch (error) {
      setResendError('Network error. Please check your connection and try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-8 md:p-12">
          {/* Success Icon */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Mail" size={40} className="text-success" />
            </div>
            
            <h1 className="text-3xl font-bold text-card-foreground mb-3">
              Check Your Email
            </h1>
            <p className="text-lg text-muted-foreground">
              We've sent a verification link to
            </p>
            <p className="text-lg font-semibold text-primary mt-2">
              {email}
            </p>
          </div>

          {/* Verification Status */}
          <div className="bg-muted/50 border border-border rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Icon name="Clock" size={24} className="text-warning" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-card-foreground mb-2">
                  Verification Pending
                </h3>
                <p className="text-sm text-muted-foreground">
                  Click the verification link in your email to complete your account setup. 
                  The link will expire in 24 hours.
                </p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-6 mb-8">
            <div>
              <h3 className="text-sm font-semibold text-card-foreground mb-3 flex items-center">
                <Icon name="CheckCircle" size={18} className="mr-2 text-primary" />
                Next Steps
              </h3>
              <ol className="space-y-3 ml-6">
                <li className="text-sm text-muted-foreground flex items-start">
                  <span className="text-primary font-semibold mr-2">1.</span>
                  <span>Check your inbox for an email from Share Good</span>
                </li>
                <li className="text-sm text-muted-foreground flex items-start">
                  <span className="text-primary font-semibold mr-2">2.</span>
                  <span>Click the verification link in the email</span>
                </li>
                <li className="text-sm text-muted-foreground flex items-start">
                  <span className="text-primary font-semibold mr-2">3.</span>
                  <span>Return here to sign in and complete your profile</span>
                </li>
              </ol>
            </div>

            <div className="bg-info/10 border border-info/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-info flex-shrink-0 mt-0.5" />
                <div className="text-sm text-info-foreground">
                  <p className="font-semibold mb-1">Didn't receive the email?</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Check your spam or junk folder</li>
                    <li>• Make sure {email} is correct</li>
                    <li>• Wait a few minutes for delivery</li>
                    <li>• Use the button below to resend</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Success/Error Messages */}
          {resendSuccess && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6 flex items-center space-x-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0" />
              <p className="text-sm text-success">
                Verification email sent successfully! Please check your inbox.
              </p>
            </div>
          )}

          {resendError && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6 flex items-center space-x-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
              <p className="text-sm text-error">{resendError}</p>
            </div>
          )}

          {/* Resend Button */}
          <div className="space-y-4">
            <Button
              onClick={handleResendVerification}
              variant="outline"
              fullWidth
              disabled={!canResend || isResending}
              loading={isResending}
              iconName="RefreshCw"
              iconPosition="left"
            >
              {isResending
                ? 'Sending...'
                : countdown > 0
                ? `Resend Available in ${countdown}s`
                : 'Resend Verification Email'}
            </Button>

            <Button
              onClick={handleBackToLogin}
              variant="ghost"
              fullWidth
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Back to Login
            </Button>
          </div>

          {/* Support Contact */}
          <div className="mt-8 pt-6 border-t border-border text-center">
            <p className="text-sm text-muted-foreground mb-2">
              Still having trouble?
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="text-sm text-primary hover:text-primary/80 transition-micro font-medium inline-flex items-center"
            >
              <Icon name="HelpCircle" size={16} className="mr-1" />
              Contact Support
            </button>
          </div>
        </div>

        {/* Email Delivery Tips */}
        <div className="mt-6 bg-muted/30 border border-border rounded-lg p-6">
          <h4 className="text-sm font-semibold text-card-foreground mb-3 flex items-center">
            <Icon name="Lightbulb" size={18} className="mr-2 text-warning" />
            Email Delivery Tips
          </h4>
          <ul className="space-y-2 text-xs text-muted-foreground">
            <li className="flex items-start">
              <Icon name="Dot" size={16} className="mr-1 flex-shrink-0" />
              <span>Verification emails typically arrive within 2-5 minutes</span>
            </li>
            <li className="flex items-start">
              <Icon name="Dot" size={16} className="mr-1 flex-shrink-0" />
              <span>Add noreply@sharegood.com to your contacts to ensure delivery</span>
            </li>
            <li className="flex items-start">
              <Icon name="Dot" size={16} className="mr-1 flex-shrink-0" />
              <span>Some email providers may delay delivery during high traffic periods</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RegistrationConfirmation;